import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import { blogPosts } from '@/data/mock';
import usePageTitle from '@/hooks/usePageTitle';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  usePageTitle(
    post ? post.title : "Article",
    post ? `${post.excerpt} ElitePlus IT consulting insights UAE.` : "ElitePlus IT consulting article."
  );

  // Inject Article schema
  useEffect(() => {
    if (!post) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.image,
      "author": { "@type": "Organization", "name": "ElitePlus Solutions" },
      "publisher": { "@type": "Organization", "name": "ElitePlus Solutions" },
      "datePublished": post.date,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { const el = document.getElementById("article-schema"); if (el) el.remove(); };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-slate-800 mb-4">Article Not Found</h1>
          <Link to="/insights" className="text-cyan-500 hover:text-cyan-600">Back to Insights</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const otherPosts = blogPosts.filter(p => p.slug !== slug).slice(-3).reverse();

  // Convert markdown-like content to HTML
  const renderContent = (content) => {
    return content.split('\n\n').map((block, i) => {
      if (block.startsWith('**') && block.endsWith('**')) {
        return <h3 key={i} className="text-xl font-bold text-slate-800 mt-8 mb-4">{block.replace(/\*\*/g, '')}</h3>;
      }
      if (block.startsWith('- ')) {
        const items = block.split('\n').filter(l => l.startsWith('- '));
        return (
          <ul key={i} className="list-disc list-inside space-y-2 mb-6 text-slate-600">
            {items.map((item, j) => <li key={j}>{item.replace(/^- \*\*(.+?)\*\*:/, '<strong>$1</strong>:').replace(/^- /, '')}</li>)}
          </ul>
        );
      }
      if (block.match(/^\d+\./)) {
        const items = block.split('\n').filter(l => l.match(/^\d+\./));
        return (
          <ol key={i} className="list-decimal list-inside space-y-2 mb-6 text-slate-600">
            {items.map((item, j) => <li key={j}>{item.replace(/^\d+\.\s*/, '')}</li>)}
          </ol>
        );
      }
      // Handle inline bold
      const parts = block.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-slate-600 leading-relaxed mb-4">
          {parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j} className="text-slate-800 font-semibold">{part.replace(/\*\*/g, '')}</strong>
              : part
          )}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/insights" className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-500 transition-colors duration-300 mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Insights
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-cyan-500/10 text-cyan-600 text-xs font-medium rounded-full">{post.category}</span>
            <span className="text-slate-400 text-sm flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
            <span className="text-slate-400 text-sm flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6 leading-tight">{post.title}</h1>
          <p className="text-xl text-slate-600 leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src={post.image}
            alt={`${post.title} - ElitePlus IT consulting UAE`}
            loading="lazy"
            decoding="async"
            className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-3xl shadow-lg"
          />
        </div>
      </section>

      {/* Content */}
      <article className="relative py-8" itemScope itemType="https://schema.org/Article">
        <meta itemProp="headline" content={post.title} />
        <meta itemProp="author" content="ElitePlus Solutions" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none" itemProp="articleBody">
            {renderContent(post.content)}
          </div>

          {/* Tags */}
          {post.tags && (
            <div className="flex flex-wrap items-center gap-2 mt-12 pt-8 border-t border-slate-200">
              <Tag className="w-4 h-4 text-slate-400" />
              {post.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">{tag}</span>
              ))}
            </div>
          )}
        </div>
      </article>

      {/* Related Posts */}
      {otherPosts.length > 0 && (
        <section className="relative py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {otherPosts.map((p) => (
                <Link key={p.id} to={`/insights/${p.slug}`} className="group">
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-cyan-300 hover:shadow-lg transition-all duration-300">
                    <img src={p.image} alt={`${p.title} - UAE IT insights`} loading="lazy" decoding="async" className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="p-5">
                      <span className="text-xs text-cyan-600 font-medium">{p.category}</span>
                      <h3 className="text-base font-bold text-slate-800 mt-1 group-hover:text-cyan-600 transition-colors leading-snug">{p.title}</h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">
            Need Help with <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">{post.category}</span>?
          </h2>
          <p className="text-slate-500 text-lg mb-8">Our senior consultants can help your UAE business navigate technology transformation.</p>
          <Link to="/contact">
            <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8">
              Book a Free Consultation
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogPostPage;
